import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Toolbar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import CustomerSidebar from '../components/navigation/CustomerSidebar';
import CustomerHeader from '../components/navigation/CustomerHeader';
import Footer from '../components/Footer';
import CreditHoldRibbon from '../components/CreditHoldRibbon';
import CreditHoldDialog from '../components/CreditHoldDialog';
import { CreditHoldProvider, useCustomerCreditHold } from '../hooks/useCustomerCreditHold';
import RouteTracker from '../components/analytics/RouteTracker';

const LoginCreditHoldGate: React.FC = () => {
  const { loginDialogPending, markLoginDialogShown } = useCustomerCreditHold();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (loginDialogPending) setOpen(true);
  }, [loginDialogPending]);

  const handleClose = () => {
    setOpen(false);
    markLoginDialogShown();
  };

  return <CreditHoldDialog open={open} onClose={handleClose} />;
};

const CustomerLayoutInner: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Fires a GA4 page view on every route change under
          CustomerLayout. Renders nothing visible. Safely no-ops on
          non-production envs (see services/analytics.ts). */}
      <RouteTracker />
      {/* Top Navbar */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar sx={{ px: { xs: 1, md: 3 }, gap: 0.5 }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <CustomerHeader />
        </Toolbar>
      </AppBar>

      {/* Mobile nav drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
            },
          }}
        >
          <CustomerSidebar onItemClick={() => setMobileOpen(false)} />
        </Drawer>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          mt: '64px',
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
          overflowX: 'hidden',
        }}
      >
        <CreditHoldRibbon />
        <Box sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1400, mx: 'auto', width: '100%' }}>
          <Outlet />
        </Box>
        <Footer variant="customer" />
      </Box>

      <LoginCreditHoldGate />
    </Box>
  );
};

const CustomerLayout: React.FC = () => (
  <CreditHoldProvider>
    <CustomerLayoutInner />
  </CreditHoldProvider>
);

export default CustomerLayout;
